// Client-side visitor + funnel analytics.
// - Persists a random `visitor_id` in localStorage.
// - Rotates a `session_id` after 30 min of inactivity.
// - Captures device, browser, os, language, screen, referrer, UTM, and ?ref= on first hit.
// - Fires PAGE_VIEW on every route change plus named funnel events.
//
// The backend accepts only a whitelist of event names — unknown events are
// dropped server-side, so we don't need to guard them here.
import { api } from "./api";

const V_KEY = "pmp.visitor";
const S_KEY = "pmp.session";
const CTX_KEY = "pmp.visitorCtx";
const SESSION_TTL_MIN = 30;

function uuid() {
  if (crypto?.randomUUID) return crypto.randomUUID();
  return "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function _now() { return Date.now(); }

function detectDevice() {
  const w = typeof window !== "undefined" ? window.innerWidth : 0;
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/iPad|Tablet/i.test(ua) || (w >= 600 && w < 1024)) return "tablet";
  if (/Mobi|Android/i.test(ua) || w < 600) return "mobile";
  return "desktop";
}
function detectBrowser() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua)) return "Safari";
  return "Other";
}
function detectOS() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iOS/.test(ua)) return "iOS";
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac OS/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Other";
}

function loadOrCreateVisitor() {
  let vid = localStorage.getItem(V_KEY);
  if (!vid) { vid = uuid(); localStorage.setItem(V_KEY, vid); }
  return vid;
}

function loadOrRotateSession() {
  try {
    const raw = localStorage.getItem(S_KEY);
    const s = raw ? JSON.parse(raw) : null;
    const now = _now();
    if (s && now - (s.last || 0) < SESSION_TTL_MIN * 60_000) {
      s.last = now;
      localStorage.setItem(S_KEY, JSON.stringify(s));
      return s.id;
    }
    const id = uuid();
    localStorage.setItem(S_KEY, JSON.stringify({ id, last: now }));
    return id;
  } catch { return uuid(); }
}

function loadOrBuildContext() {
  let ctx;
  try { ctx = JSON.parse(localStorage.getItem(CTX_KEY) || "null"); } catch { ctx = null; }
  if (ctx?.locked) return ctx;
  const url = new URL(window.location.href);
  const q = url.searchParams;
  ctx = {
    device_type: detectDevice(),
    browser: detectBrowser(),
    os: detectOS(),
    screen_size: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    language: (navigator.language || "").slice(0, 10),
    referrer: (document.referrer || "").slice(0, 500),
    landing_page: url.pathname + url.search,
    utm_source: q.get("utm_source") || null,
    utm_medium: q.get("utm_medium") || null,
    utm_campaign: q.get("utm_campaign") || null,
    utm_content: q.get("utm_content") || null,
    referral_code: q.get("ref") || null,
    locked: true,
  };
  localStorage.setItem(CTX_KEY, JSON.stringify(ctx));
  return ctx;
}

function baseCtx() {
  return {
    visitor_id: loadOrCreateVisitor(),
    session_id: loadOrRotateSession(),
    ...loadOrBuildContext(),
  };
}

export function track(event, extra = {}) {
  try {
    const payload = { ...baseCtx(), event, page: window.location.pathname + window.location.search, ...extra };
    // Fire-and-forget, silently swallow errors (analytics must never break UX).
    api.analyticsTrack(payload);
  } catch { /* ignore */ }
}

export function pageView(path) {
  track("PAGE_VIEW", { page: path || (window.location.pathname + window.location.search) });
}

// Convenience: sends a named event from a component (React-safe).
export const Analytics = { track, pageView };
